import { useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Paperclip,
  PackageX,
  ShoppingBag,
  Truck,
  X,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { AREA_BY_ID, RESTAURANT_BY_ID } from "../../constants";
import { canApprove, canConfirmDelivery, isMain, lineShortfall } from "../../lib/permissions";

function effectiveLineStatus(line, request) {
  return line.status || (line.purchased ? "completed" : request.status);
}

const requestDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatRequestDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : requestDateFormatter.format(date);
}

// Collapse N purchase requests for the same supplier into one consolidated view.
function consolidateRequests(requests, productById) {
  const acc = {};
  requests.forEach((r) =>
    r.items?.forEach((it, itemIndex) => {
      const lineStatus = effectiveLineStatus(it, r);
      if (lineStatus === "completed" || lineStatus === "fulfilled" || lineStatus === "rejected" || lineStatus === "supplier_unavailable") return;
      const p = productById(it.productId);
      if (!p) return;
      if (!acc[p.id]) acc[p.id] = { product: p, totalQty: 0, lines: [] };
      acc[p.id].totalQty += it.quantity ?? it.qty ?? 0;
      acc[p.id].lines.push({
        ...it,
        request: r,
        itemIndex,
        lineKey: it.itemId || `${it.productId || it.productName || "item"}::${itemIndex}`,
      });
    })
  );
  return Object.values(acc).sort((a, b) => a.product.name.localeCompare(b.product.name));
}

// Bucket purchase requests by supplier AND destination restaurant. This prevents
// orders for Spring Mountain and Henderson from ever being consolidated together.
function compareSuppliers(a, b) {
  if (a.supplier.type !== b.supplier.type) {
    return a.supplier.type === "delivered" ? -1 : 1;
  }
  return a.supplier.name.localeCompare(b.supplier.name);
}

function groupBySupplier(filtered, supplierById) {
  const map = {};
  filtered
    .filter((r) => r.type === "purchase")
    .forEach((r) => {
      const sup = supplierById(r.vendor);
      if (!sup) return;
      const key = `${sup.id}::${r.restaurant || "unknown"}`;
      map[key] = map[key] || { supplier: sup, restaurantId: r.restaurant, requests: [] };
      map[key].requests.push(r);
    });
  return Object.values(map).sort(compareSuppliers);
}

function areasFor(request) {
  const list =
    request.requesterAreas && request.requesterAreas.length > 0
      ? request.requesterAreas
      : request.requesterArea
      ? [request.requesterArea]
      : [];
  return list.map((a) => AREA_BY_ID[a]?.name).filter(Boolean).join(" / ");
}

// Map a request status to a badge color scheme. Lookup table is clearer
// than a chained ternary and easier to extend later.
const STATUS_BADGE_CLASS = {
  fulfilled: "border-[#2B7A6B]/40 text-[#2B7A6B] bg-[#E3F0EC]",
  approved: "border-[#2B7A6B]/40 text-[#2B7A6B] bg-[#E3F0EC]",
  completed: "border-[#2B7A6B]/40 text-[#2B7A6B] bg-[#E3F0EC]",
  partially_fulfilled: "border-[#D48E36]/40 text-[#A56614] bg-[#FDEFCF]",
  missed: "border-red-300 text-red-600 bg-red-50",
  rejected: "border-red-300 text-red-600 bg-red-50",
  supplier_unavailable: "border-amber-400 text-amber-700 bg-amber-50",
};
const DEFAULT_BADGE = "border-[#E6E0D4] text-[#0B1F3A]/70 bg-[#F3EFE7]";
function statusBadgeClass(status) { return STATUS_BADGE_CLASS[status] || DEFAULT_BADGE; }

function LineChip({ line, productId, user, onApproveLine, onRejectLine, onUnavailableLine, onDeleteLine, onConfirmLine, onToggleLinePurchased }) {
  const r = line.request;
  const rest = RESTAURANT_BY_ID[r.restaurant];
  const areaList = areasFor(r);
  const lineStatus = effectiveLineStatus(line, r);
  const pending = lineStatus === "pending" || lineStatus === "submitted";
  const ordered = Number(line.quantity ?? line.qty ?? 0);
  const confirmed = !!r.deliveryConfirmedAt;
  const missing = confirmed ? lineShortfall(line) : 0;
  const approvedUnconfirmed = lineStatus === "approved" && !confirmed;
  const purchased = !!line.purchased;
  const controlClass = "h-8 w-8 sm:h-5 sm:w-5 shrink-0 rounded-full bg-white border border-[#E6E0D4] flex items-center justify-center transition-colors";

  return (
    <div className="group w-full max-w-full min-w-0 text-xs bg-[#F3EFE7] rounded-xl sm:rounded-full px-2.5 py-2 sm:py-1 flex flex-wrap items-center gap-x-1.5 gap-y-2 border border-[#E6E0D4] overflow-hidden">
      <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span className="text-[#0B1F3A]/70">{rest?.name}</span>
        <span className="text-[#0B1F3A]/40">·</span>
        <span className="font-medium">{ordered}</span>
        <span className="text-[#0B1F3A]/50">
          · {r.requestedByName}
          {areaList ? ` · ${areaList}` : ""}
        </span>
        <span className="text-[#0B1F3A]/40 whitespace-nowrap">· {formatRequestDate(r.createdAt)}</span>
      </div>

      <div className="ml-auto shrink-0 flex flex-wrap items-center justify-end gap-1">
        {canConfirmDelivery(user, r) && (lineStatus === "approved" || lineStatus === "completed" || lineStatus === "fulfilled" || lineStatus === "partially_fulfilled") && (
          <button
            data-testid={`purchase-line-${r.id}-${line.lineKey}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleLinePurchased?.(r, line.lineKey, !purchased);
            }}
            className={`${controlClass} px-1.5 ${
              purchased
                ? "bg-[#2B7A6B] border-[#2B7A6B] text-white"
                : "hover:border-[#2B7A6B] hover:text-[#2B7A6B]"
            }`}
            title={purchased ? "Mark this item pending" : "Mark this item purchased"}
            aria-label={purchased ? "Mark this item pending" : "Mark this item purchased"}
          >
            <Check size={12} />
          </button>
        )}

        {pending && canApprove(user, r) ? (
          <>
            <button
              data-testid={`approve-line-${r.id}-${productId}`}
              onClick={() => onApproveLine(r, line.lineKey)}
              className={`${controlClass} hover:border-[#2B7A6B] hover:text-[#2B7A6B]`}
              title="Approve"
              aria-label="Approve"
            >
              <Check size={12} />
            </button>
            <button
              data-testid={`reject-line-${r.id}-${productId}`}
              onClick={() => onRejectLine(r, line.lineKey)}
              className={`${controlClass} hover:border-red-400 hover:text-red-500`}
              title="Reject"
              aria-label="Reject"
            >
              <X size={12} />
            </button>
            <button
              data-testid={`unavailable-line-${r.id}-${line.lineKey}`}
              onClick={() => onUnavailableLine(r, line.lineKey)}
              className={`${controlClass} hover:border-amber-500 hover:text-amber-600`}
              title="Unavailable at supplier"
              aria-label="Unavailable at supplier"
            >
              <PackageX size={12} />
            </button>
          </>
        ) : (
          <Badge
            variant="outline"
            className={`h-6 sm:h-5 px-1.5 text-[10px] whitespace-nowrap ${statusBadgeClass(lineStatus)}`}
          >
            {lineStatus}
          </Badge>
        )}

        {confirmed && (
          <span
            className={`text-[10px] whitespace-nowrap ${missing > 0 ? "text-[#C4552B]" : "text-[#2B7A6B]"}`}
            title={`Received ${line.receivedQty ?? 0} of ${ordered}`}
          >
            {missing > 0 ? `(-${missing})` : "✓"}
          </span>
        )}
        {confirmed && r.invoiceFile && (
          <a
            data-testid={`invoice-open-${r.id}-${productId}`}
            href={r.invoiceFile}
            target="_blank"
            rel="noopener noreferrer"
            download={r.invoiceFileName || `invoice-${r.invoiceNumber || r.id}.pdf`}
            onClick={(e) => e.stopPropagation()}
            title={`Open invoice ${r.invoiceNumber || ""}`.trim()}
            aria-label="Open invoice"
            className={`${controlClass} hover:border-[#0B1F3A]/60 text-[#0B1F3A]`}
          >
            <Paperclip size={12} />
          </a>
        )}
        {approvedUnconfirmed && canConfirmDelivery(user, r) && (
          <button
            data-testid={`confirm-line-${r.id}-${productId}`}
            onClick={() => onConfirmLine?.(r)}
            className={`${controlClass} hover:border-[#0B1F3A]/60 text-[#0B1F3A]`}
            title="Confirm delivery"
            aria-label="Confirm delivery"
          >
            <FileText size={12} />
          </button>
        )}
        {isMain(user) && (
          <button
            data-testid={`delete-line-${r.id}-${line.lineKey}`}
            onClick={() => onDeleteLine(r, line.lineKey)}
            className={`${controlClass} border-transparent bg-transparent text-[#0B1F3A]/40 hover:bg-white hover:text-[#0B1F3A]`}
            title="Remove"
            aria-label="Remove"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function ConsolidatedProductRow({ item, user, onApproveLine, onRejectLine, onUnavailableLine, onDeleteLine, onConfirmLine, onToggleLinePurchased }) {
  const notedLines = item.lines.filter((ln) => ln.note);
  return (
    <div className="px-4 md:px-5 py-4 border-b last:border-b-0 border-[#F2ECDE]">
      <div className="flex items-start gap-3 md:gap-4">
        {item.product.image ? (
          <img
            src={item.product.image}
            alt=""
            className="h-12 w-12 rounded-lg object-cover border border-[#EEE8DC] shrink-0"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="h-12 w-12 rounded-lg bg-[#F3EFE7] border border-[#EEE8DC] shrink-0" />
        )}
        <div className="h-9 min-w-9 px-2 rounded-full bg-[#0B1F3A] text-white text-sm font-medium flex items-center justify-center shrink-0">
          {item.totalQty}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">{item.product.category}</p>
          <p className="text-sm font-medium">
            {item.product.name}{" "}
            <span className="text-[#0B1F3A]/55 font-normal">· {item.product.unit}</span>
          </p>
          <div className="mt-2 flex min-w-0 flex-col gap-2">
            {item.lines.map((ln) => (
              <LineChip
                key={`${ln.request.id}-${ln.lineKey}`}
                line={ln}
                productId={item.product.id}
                user={user}
                onApproveLine={onApproveLine}
                onRejectLine={onRejectLine}
                onUnavailableLine={onUnavailableLine}
                onDeleteLine={onDeleteLine}
                onConfirmLine={onConfirmLine}
                onToggleLinePurchased={onToggleLinePurchased}
              />
            ))}
          </div>
          {notedLines.length > 0 && (
            <div className="mt-2 space-y-1">
              {notedLines.map((ln) => (
                <p
                  key={`note-${ln.request.id}-${ln.lineKey}`}
                  className="text-xs text-[#0B1F3A]/65 italic"
                >
                  “{ln.note}” — {ln.request.requestedByName}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SupplierGroup({ group, user, onApproveLine, onRejectLine, onUnavailableLine, onDeleteLine, onApproveAll, productById, onConfirmGroup, onConfirmLine, onToggleLinePurchased }) {
  const restaurant = RESTAURANT_BY_ID[group.restaurantId];
  const restaurantTone = group.restaurantId === "restaurant_2"
    ? "border-[#C4552B]/45 bg-[#F7E4DB] text-[#9F3F20]"
    : "border-[#315F9B]/40 bg-[#E6E9F4] text-[#244C80]";
  const [open, setOpen] = useState(true);
  const consolidated = useMemo(
    () => consolidateRequests(group.requests, productById),
    [group.requests, productById]
  );
  const totalUnits = consolidated.reduce((s, c) => s + c.totalQty, 0);
  const anyPending = group.requests.some(
    (r) => canApprove(user, r) && (r.items || []).some((line) => {
      const status = effectiveLineStatus(line, r);
      return status === "submitted" || status === "pending";
    })
  );
  const approvedAwaiting = group.requests.filter(
    (r) => r.status === "approved" && !r.deliveryConfirmedAt && canConfirmDelivery(user, r)
  );
  const approveAll = (e) => {
    e.stopPropagation();
    onApproveAll(group);
  };
  const confirmGroup = (e) => {
    e.stopPropagation();
    onConfirmGroup?.(group, approvedAwaiting);
  };
  const toggle = () => setOpen((v) => !v);
  return (
    <div className="bg-white border border-[#E6E0D4] rounded-xl overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        data-testid={`supplier-group-${group.supplier.id}-${group.restaurantId}`}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className="w-full flex items-center gap-3 md:gap-4 px-4 md:px-5 py-4 hover:bg-[#FBF8F1] transition-colors text-left cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B1F3A]/30"
      >
        <div
          className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
            group.supplier.type === "delivered"
              ? "bg-[#E3F0EC] text-[#2B7A6B]"
              : "bg-[#F7E4DB] text-[#C4552B]"
          }`}
        >
          {group.supplier.type === "delivered" ? (
            <Truck size={18} strokeWidth={1.8} />
          ) : (
            <ShoppingBag size={18} strokeWidth={1.8} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-medium">{group.supplier.name}</p>
            <Badge variant="outline" className={`font-semibold ${restaurantTone}`}>
              {restaurant?.name || "Unknown restaurant"}
            </Badge>
            <Badge
              variant="outline"
              className={
                group.supplier.type === "delivered"
                  ? "border-[#2B7A6B]/40 text-[#2B7A6B] bg-[#E3F0EC]"
                  : "border-[#C4552B]/40 text-[#C4552B] bg-[#F7E4DB]"
              }
            >
              {group.supplier.type === "delivered" ? "Delivered" : "Direct"}
            </Badge>
            <span className="text-xs text-[#0B1F3A]/55 hidden sm:inline">· {group.supplier.lead}</span>
          </div>
          <p className="text-xs text-[#0B1F3A]/60 mt-0.5">
            {consolidated.length} SKU· {totalUnits} units · {group.requests.length} request
            {group.requests.length > 1 ? "s" : ""}
          </p>
        </div>
        {anyPending && (
          <Button
            size="sm"
            data-testid={`approve-all-${group.supplier.id}-${group.restaurantId}`}
            onClick={approveAll}
            className="bg-[#0B1F3A] hover:bg-[#11294A] text-white px-2.5 sm:px-3"
          >
            <Check size={14} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Approve all</span>
          </Button>
        )}
        {approvedAwaiting.length > 0 && (
          <Button
            size="sm"
            data-testid={`confirm-delivery-${group.supplier.id}-${group.restaurantId}`}
            onClick={confirmGroup}
            variant="outline"
            className="border-[#0B1F3A]/30 bg-white hover:bg-[#F3EFE7] text-[#0B1F3A] px-2.5 sm:px-3"
          >
            <FileText size={14} className="sm:mr-1.5" />
            <span className="hidden sm:inline">Confirm delivery</span>
          </Button>
        )}
        {open ? (
          <ChevronDown size={18} className="text-[#0B1F3A]/50 shrink-0" />
        ) : (
          <ChevronRight size={18} className="text-[#0B1F3A]/50 shrink-0" />
        )}
      </div>
      {open && (
        <div className="border-t border-[#EEE8DC]">
          {consolidated.map((c) => (
            <ConsolidatedProductRow
              key={c.product.id}
              item={c}
              user={user}
              onApproveLine={onApproveLine}
              onRejectLine={onRejectLine}
              onUnavailableLine={onUnavailableLine}
              onDeleteLine={onDeleteLine}
              onConfirmLine={onConfirmLine}
              onToggleLinePurchased={onToggleLinePurchased}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PurchaseGroups({ filtered, suppliers, products, user, onApproveLine, onRejectLine, onUnavailableLine, onDeleteLine, onApproveAll, onConfirmGroup, onConfirmLine, onToggleLinePurchased }) {
  const supplierById = useMemo(() => {
    const map = new Map(suppliers.map((s) => [s.id, s]));
    return (id) => map.get(id);
  }, [suppliers]);
  const productById = useMemo(() => {
    const map = new Map(products.map((p) => [p.id, p]));
    return (id) => map.get(id);
  }, [products]);
  const grouped = useMemo(() => groupBySupplier(filtered, supplierById), [filtered, supplierById]);
  if (grouped.length === 0) return null;
  return (
    <div className="space-y-3 mb-6" data-testid="purchase-groups">
      {grouped.map((g) => (
        <SupplierGroup
          key={`${g.supplier.id}-${g.restaurantId}`}
          group={g}
          user={user}
          onApproveLine={onApproveLine}
          onRejectLine={onRejectLine}
          onUnavailableLine={onUnavailableLine}
          onDeleteLine={onDeleteLine}
          onApproveAll={onApproveAll}
          onConfirmGroup={onConfirmGroup}
          onConfirmLine={onConfirmLine}
          onToggleLinePurchased={onToggleLinePurchased}
          productById={productById}
        />
      ))}
    </div>
  );
}
